import PaymentDrawer, {
  CheckoutProductSummary,
} from "@/_components/ui/PaymentsUI/PaymentDrawer";
import { PurchaseSuccessDialog } from "@/_components/ui/paymentDialogs/PurchaseSuccessDialog";

import { FRAME_TARGET, usePayment } from "./usePayment";
import { PaddleEventData } from "@paddle/paddle-js";
import { useCallback, useMemo, useState } from "react";
import { useRouter } from "next/navigation";

/** Payload shape for `checkout.discount.applied` / `checkout.discount.removed` */
type PaddleDiscountCheckoutPayload = {
  totals: {
    subtotal: number;
    tax: number;
    total: number;
    discount?: number;
  };
  currency_code: string;
  items?: CheckoutProductSummaryItem[];
  discount?: { id?: string; code?: string };
};

type CheckoutProductSummaryItem = {
  price_name?: string;
  product: { image_url?: string; name: string };
};

interface Options {
  title?: string;
  showSuccessDialogOnPurchase?: boolean;
  onPurchaseSuccessConfirm?: () => void;
}

export const usePaymentCheckout = (opts: Options) => {
  const [isPaymentLoading, setIsPaymentLoading] = useState(true);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [checkoutProduct, setCheckoutProduct] =
    useState<CheckoutProductSummary | null>(null);
  const router = useRouter();

  const handleCheckoutEvent = useCallback(
    (event: PaddleEventData) => {
      if (!event.data) {
        return;
      }

      switch (event.name) {
        case "checkout.loaded": {
          setIsPaymentLoading(false);
          const firstItem = event.data.items?.[0];
          if (!firstItem) {
            return;
          }

          setCheckoutProduct({
            imageUrl: firstItem.product.image_url,
            name: firstItem.product.name,
            price_cycle: firstItem.price_name ?? "monthly",
            subtotal: event.data.totals.subtotal,
            tax: event.data.totals.tax,
            discount: event.data.totals.discount ?? 0,
            discountCode: event.data.discount?.code,
            total: event.data.totals.total,
            currencyCode: event.data.currency_code,
          });
          break;
        }
        case "checkout.discount.applied":
        case "checkout.discount.removed": {
          const payload = event.data as PaddleDiscountCheckoutPayload;
          if (!payload.totals) {
            return;
          }

          const isApplied = event.name === "checkout.discount.applied";

          setCheckoutProduct((prev) => {
            const firstItem = payload.items?.[0];
            const nextTotals = payload.totals;
            const nextDiscount = nextTotals.discount ?? 0;

            if (prev) {
              return {
                ...prev,
                subtotal: nextTotals.subtotal,
                tax: nextTotals.tax,
                total: nextTotals.total,
                discount: nextDiscount,
                discountCode: isApplied ? payload.discount?.code : undefined,
                currencyCode: payload.currency_code ?? prev.currencyCode,
              };
            }

            if (!firstItem) {
              return prev;
            }

            return {
              imageUrl: firstItem.product.image_url,
              name: firstItem.product.name,
              price_cycle: firstItem.price_name ?? "monthly",
              subtotal: nextTotals.subtotal,
              tax: nextTotals.tax,
              total: nextTotals.total,
              discount: nextDiscount,
              discountCode: isApplied ? payload.discount?.code : undefined,
              currencyCode: payload.currency_code,
            };
          });
          break;
        }
        case "checkout.closed":
          setIsPaymentLoading(false);
          break;
        case "checkout.completed": {
          if (opts.showSuccessDialogOnPurchase) {
            setIsCheckoutOpen(false);
            setIsSuccessDialogOpen(true);
            break;
          }

          setTimeout(() => {
            router.push("/dashboard");
          }, 3000);
          break;
        }
        default:
          break;
      }
    },
    [opts.showSuccessDialogOnPurchase, router],
  );

  const {
    openCheckout,
    closeCheckout,
    isPaddleReady,
    openUpdatePaymentDetail,
  } = usePayment(handleCheckoutEvent);

  const checkoutCurrency = useMemo(() => {
    if (!checkoutProduct) {
      return null;
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: checkoutProduct.currencyCode,
      maximumFractionDigits: 2,
    });
  }, [checkoutProduct]);

  const handleCheckoutOpenChange = useCallback(
    (open: boolean) => {
      setIsCheckoutOpen(open);
      if (!open) {
        closeCheckout();
      }
      setCheckoutProduct(null);
    },
    [closeCheckout],
  );

  const openCheckoutPanel = useCallback(
    (planId: string) => {
      setIsPaymentLoading(true);
      setIsCheckoutOpen(true);
      setTimeout(() => {
        openCheckout({ priceId: planId, quantity: 1 });
      }, 100);
    },
    [openCheckout],
  );

  const onOpenUpdatePaymentDetail = useCallback(() => {
    setIsPaymentLoading(true);
    setIsCheckoutOpen(true);
    setTimeout(() => {
      openUpdatePaymentDetail();
    }, 100);
  }, [openUpdatePaymentDetail]);

  const Drawer = useMemo(
    () => (
      <PaymentDrawer
        isOpen={isCheckoutOpen}
        onOpenChange={handleCheckoutOpenChange}
        checkoutProduct={checkoutProduct}
        checkoutCurrency={checkoutCurrency}
        frameTarget={FRAME_TARGET}
        isPaymentLoading={isPaymentLoading}
        title={opts.title}
      />
    ),
    [
      isCheckoutOpen,
      handleCheckoutOpenChange,
      checkoutProduct,
      checkoutCurrency,
      isPaymentLoading,
    ],
  );

  const handlePurchaseSuccessConfirm = useCallback(() => {
    if (opts.onPurchaseSuccessConfirm) {
      opts.onPurchaseSuccessConfirm();
      return;
    }

    router.push("/dashboard");
  }, [opts.onPurchaseSuccessConfirm, router]);

  const SuccessDialog = useMemo(
    () => (
      <PurchaseSuccessDialog
        open={isSuccessDialogOpen}
        onOpenChange={setIsSuccessDialogOpen}
        productName={checkoutProduct?.name ?? ""}
        totalAmount={String(checkoutProduct?.total ?? "0")}
        currencyCode={checkoutProduct?.currencyCode ?? "USD"}
        onConfirm={handlePurchaseSuccessConfirm}
      />
    ),
    [checkoutProduct, handlePurchaseSuccessConfirm, isSuccessDialogOpen],
  );

  return {
    Drawer,
    SuccessDialog,
    isPaddleReady,
    openCheckoutPanel,
    onOpenUpdatePaymentDetail,
  };
};
